import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileExplorer as UIFileExplorer, FileTree, type FileNode } from '@ncoder/ui';
import { withRulesFolder, rulesFolderNode } from '@/services/rules/ids';
import { CreateMetadataModal } from '@/components/molecules/metadata_v1/CreateMetadataModal';
import type { CreateMetadataFormData } from '@/components/molecules/metadata_v1/CreateMetadataModal';
import { createMetadataLoadChildren } from '@/hooks/metadata_v1/createMetadataLoadChildren';
import { useMetadataEditorStore } from '@/stores/metadataEditorStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useStartRetrieval, useComponentTemplate, useCancelRetrieval } from '@/hooks/metadata_v1';
import { useChatPendingInputStore } from '@/stores/chatPendingInputStore';
import { RefreshType } from '@/constants/metadata/refresh_type';
import { useRetrievalProgressStore } from '@/stores/retrievalProgressStore';
import { useRetrievalProgressInitialStore } from '@/stores/retrievalProgressInitialStore';
import { useActivityStore } from '@/stores/activityStore';
import toast from 'react-hot-toast';
import { extractComponentName } from '@/constants/metadata/bundleTypes';

interface FileExplorerProps {
  files: FileNode[];
  isLoading?: boolean;
  selectedFile?: FileNode | null;
  expandedFolders: Set<string>;
  onFileSelect?: (file: FileNode) => void;
  /** Double-click a file — open a tab that the next preview will not replace. */
  onFileOpen?: (file: FileNode) => void;
  onToggleFolder?: (folderId: string) => void;
  onOpenQuickOpen?: () => void;
  /** Total count of all metadata types (for filter badge) */
  totalMetadataTypes?: number;
  /** When true, show initial "Fetch metadata" CTA in empty state. */
  hasNoRetrieval?: boolean;
  /** Start initial metadata fetch (and connect to progress SSE) */
  onFetchMetadata?: () => void | Promise<void>;
  /** When true, disable Fetch metadata button (fetch in progress) */
  isFetchingMetadata?: boolean;
  /** File ID to reveal (scroll to) in the tree — typically the active editor tab's file. */
  revealFileId?: string;
  /**
   * Called when a bundle folder's children are loaded by the tree.
   * Provides the bundle folder ID and its child FileNodes to the parent
   * so it can build a registry for bundle context navigation.
   */
  onBundleChildrenLoaded?: (folderId: string, children: FileNode[]) => void;
}

export function FileExplorer({
  files,
  isLoading = false,
  selectedFile,
  expandedFolders,
  onFileSelect,
  onFileOpen,
  onToggleFolder,
  onOpenQuickOpen,
  totalMetadataTypes = 0,
  hasNoRetrieval = false,
  onFetchMetadata,
  isFetchingMetadata = false,
  revealFileId,
  onBundleChildrenLoaded,
}: FileExplorerProps) {
  const isMac =
    typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');
  const filterShortcutLabel = isMac ? '⌘⇧F' : 'Ctrl+Shift+F';

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalMetadataType, setCreateModalMetadataType] = useState<string | null>(null);

  const visibleMetadataTypes = useMetadataEditorStore((state) => state.visibleMetadataTypes);
  const setFilterModalOpen = useMetadataEditorStore((state) => state.setFilterModalOpen);

  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const orgId = selectedOrg?.id;
  const retrievalId = selectedOrg?.latestMetadataRetrieval?.id;
  const retrievalStatus = selectedOrg?.latestMetadataRetrieval?.status;
  const retrievalType = selectedOrg?.latestMetadataRetrieval?.retrievalType;

  const navigate = useNavigate();
  const setMultiAgentIntent = useChatPendingInputStore((s) => s.setMultiAgentIntent);
  const { startRetrieval } = useStartRetrieval();
  const { cancelRetrieval } = useCancelRetrieval();
  const { getComponentTemplate, isPending: isGeneratingTemplate } = useComponentTemplate();

  const {
    connect: connectRefreshSSE,
    disconnect: disconnectRefreshSSE,
    isConnected: sseIsConnected,
    retrievalId: sseRetrievalId,
    progressPercentage: sseProgressPercentage,
    message: sseMessage,
    totalItems: sseTotalItems,
    processedItems: sseProcessedItems,
    phaseDetails: ssePhaseDetails,
    status: sseStatus,
    lastRefreshCompletedAt,
  } = useRetrievalProgressStore();

  const hasAnyActivityRunning = useActivityStore((state) => state.isAnyActivityRunning());

  const {
    disconnect: disconnectInitialSSE,
    isConnected: initialSseConnected,
    retrievalId: initialSseRetrievalId,
    progressPercentage: initialProgressPercentage,
    message: initialMessage,
    totalItems: initialTotalItems,
    processedItems: initialProcessedItems,
    currentPhase: initialCurrentPhase,
  } = useRetrievalProgressInitialStore();

  // Treat as in-progress if: SSE is connected, or the org already reports queued/in-progress
  // while the Cloud Run metadata worker is starting or running.
  const isInProgress =
    sseIsConnected ||
    sseStatus === 'in_progress' ||
    retrievalStatus === 'QUEUED' ||
    retrievalStatus === 'IN_PROGRESS';

  // Show initial sync in bar when: (1) initial SSE is connected (e.g. right after "Fetch metadata"),
  // or (2) org reports INITIAL_RETRIEVAL + active status (e.g. after page reload before reconnect).
  const isInitialSyncInProgress =
    initialSseConnected ||
    (retrievalType === 'INITIAL_RETRIEVAL' &&
      (retrievalStatus === 'QUEUED' || retrievalStatus === 'IN_PROGRESS'));

  const syncState: 'syncing' | 'success' | 'error' | 'idle' =
    isInProgress || isInitialSyncInProgress
      ? 'syncing'
      : 'idle';

  const getRelativeTime = (date: Date | null) => {
    if (!date) return 'Sync';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const handleSync = async () => {
    if (syncState === 'syncing') return;
    if (!orgId || !retrievalId) return;
    try {
      const result = await startRetrieval({
        organizationId: orgId,
        retrievalId: retrievalId,
        refreshType: RefreshType.FULL_ORG_REFRESH,
      });
      connectRefreshSSE(result.retrievalId);
    } catch (error) {
      console.error('❌ Failed to start sync:', error);
    }
  };

  const handleCancel = async () => {
    const activeRetrievalId = isInitialSyncInProgress
      ? (initialSseRetrievalId ?? retrievalId)
      : (sseRetrievalId ?? retrievalId);
    if (!activeRetrievalId) return;

    // Disconnect SSE before awaiting the cancel API so the server's `complete`
    // event is never processed — otherwise the activity gets marked `completed`
    // (hidden by ActivityDropdown) before we can mark it `failed`.
    if (isInitialSyncInProgress) {
      disconnectInitialSSE();
    } else {
      disconnectRefreshSSE();
    }

    try {
      await cancelRetrieval({ id: activeRetrievalId });
      const { failActivity, removeActivity } = useActivityStore.getState();
      failActivity(activeRetrievalId, 'Sync cancelled');
      setTimeout(() => removeActivity(activeRetrievalId), 3_000);
    } catch (error) {
      console.error('❌ Failed to cancel sync:', error);
      toast.error('Failed to cancel sync');
      const { failActivity } = useActivityStore.getState();
      failActivity(activeRetrievalId, 'Cancel failed');
    }
  };

  const loadChildren = useMemo(() => {
    const inner = createMetadataLoadChildren({ retrievalId });
    if (!onBundleChildrenLoaded) return inner;
    // Wrap to intercept bundle folder results and populate the registry in the parent.
    return async (folder: FileNode, options?: { after?: string }) => {
      const result = await inner(folder, options);
      result.items.forEach(item => {
        if (item.type === 'folder' && item.children && item.children.length > 0) {
          onBundleChildrenLoaded(item.id, item.children);
        }
      });
      return result;
    };
  }, [retrievalId, onBundleChildrenLoaded]);

  const handleCopyName = useCallback((file: FileNode) => {
    const name = extractComponentName(file.name);
    navigator.clipboard.writeText(name).catch(() => {});
    toast.success(`Copied "${name}"`);
  }, []);

  const handleRefreshMetadataType = async (metadataType: string) => {
    if (syncState === 'syncing' || hasAnyActivityRunning) {
      if (hasAnyActivityRunning) {
        toast.error('Please wait for the current activity to finish before refreshing.', {
          duration: 4000,
          position: 'top-right',
        });
      }
      return;
    }
    if (!orgId || !retrievalId) return;
    try {
      const result = await startRetrieval({
        organizationId: orgId,
        retrievalId: retrievalId,
        refreshType: RefreshType.METADATA_TYPE,
        metadataType: metadataType,
      });
      connectRefreshSSE(result.retrievalId, { metadataType });
    } catch (error) {
      console.error('❌ Failed to refresh metadata type:', error);
    }
  };

  const handleCreateItem = useCallback((metadataType: string) => {
    setCreateModalMetadataType(metadataType);
    setCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setCreateModalMetadataType(null);
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: CreateMetadataFormData) => {
      if (!createModalMetadataType) return;
      let aiPrompt: string;
      const infoRows = [
        `| Field | Value |`,
        `|---|---|`,
        `| Type | ${createModalMetadataType} |`,
        `| Name | ${data.name} |`,
        `| Description | ${data.description.replace(/\|/g, "/").replace(/\n/g, " ")} |`,
      ];
      if (data.relatedObjects?.length) {
        infoRows.push(`| Related Objects | ${data.relatedObjects.join(", ")} |`);
      }
      const infoTable = infoRows.join("\n");
      try {
        const template = await getComponentTemplate({
          metadataType: createModalMetadataType,
          name: data.name,
          description: data.description,
          relatedObjects: data.relatedObjects,
        });
        aiPrompt = `Create a new ${createModalMetadataType} called "${data.name}":\n\n${infoTable}\n\n${template}`;
      } catch {
        aiPrompt = `Create a new ${createModalMetadataType} called "${data.name}":\n\n${infoTable}\n\nGenerate the complete code.`;
      }
      handleCloseCreateModal();
      setMultiAgentIntent(aiPrompt);
      navigate('/weave');
    },
    [createModalMetadataType, getComponentTemplate, handleCloseCreateModal, navigate, setMultiAgentIntent]
  );

  const activeFilterCount =
    visibleMetadataTypes === null ? totalMetadataTypes : visibleMetadataTypes.size;
  const isFilterActive =
    visibleMetadataTypes !== null && visibleMetadataTypes.size < totalMetadataTypes;

  // Pinned "Rules" folder: prepended so it is always first and never filtered away. With no
  // metadata at all the UI explorer would show its empty state instead of the tree, so in that
  // case the tree slot carries the Rules folder followed by the same empty state.
  const metadataEmpty = files.length === 0;
  const explorerFiles = useMemo(() => withRulesFolder(files), [files]);
  const emptyState =
    hasNoRetrieval && onFetchMetadata
      ? (
          <div className="px-3 py-6 text-center">
            <p className="text-xs text-muted-foreground mb-3">Fetch your metadata to get started.</p>
            <button
              type="button"
              onClick={onFetchMetadata}
              disabled={isFetchingMetadata}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
            >
              {isFetchingMetadata ? 'Fetching...' : 'Fetch metadata'}
            </button>
          </div>
        )
      : null;

  return (
    <>
    <UIFileExplorer
      key={`file-tree-${retrievalId ?? ''}-${lastRefreshCompletedAt ?? 0}`}
      files={explorerFiles}
      isLoading={isLoading}
      selectedFile={selectedFile}
      expandedFolders={expandedFolders}
      onFileSelect={onFileSelect}
      onFileOpen={onFileOpen}
      onToggleFolder={onToggleFolder}
      loadChildren={loadChildren}
      onRefreshMetadataType={handleRefreshMetadataType}
      onAddFile={handleCreateItem}
      refreshingType={null}
      isSyncInProgress={isInProgress}
      revealFileId={revealFileId}
      onCopyName={handleCopyName}
      title="Explorer"
      filter={{
        onClick: () => setFilterModalOpen(true),
        activeCount: activeFilterCount,
        totalCount: totalMetadataTypes,
        shortcutLabel: filterShortcutLabel,
        isActive: isFilterActive,
      }}
      itemCount={!isLoading && files.length > 0 ? files.length : undefined}
      syncStatusBar={{
        syncState,
        syncProgress: isInitialSyncInProgress
          ? initialProgressPercentage
          : sseIsConnected
            ? sseProgressPercentage
            : (selectedOrg?.latestMetadataRetrieval?.progressPercentage ?? 0),
        syncMessage: isInitialSyncInProgress
          ? (initialSseConnected ? (initialMessage ?? 'Initial sync...') : 'Initial sync...')
          : sseIsConnected
            ? (sseMessage ?? 'Refreshing...')
            : isInProgress
              ? 'Refreshing...'
              : '',
        totalItems: isInitialSyncInProgress ? initialTotalItems : sseTotalItems,
        processedItems: isInitialSyncInProgress ? initialProcessedItems : sseProcessedItems,
        currentType: isInitialSyncInProgress
          ? (initialCurrentPhase ?? 'Setting up...')
          : (ssePhaseDetails?.current_type ?? null),
        lastSyncTime: null,
        onSync: handleSync,
        onCancel: handleCancel,
        getRelativeTime,
        disabled: !orgId || hasAnyActivityRunning,
      }}
      renderEmptyState={emptyState ? () => emptyState : undefined}
    >
      {!isLoading && metadataEmpty ? (
        <>
          <FileTree
            files={[rulesFolderNode()]}
            selectedFile={selectedFile}
            expandedFolders={expandedFolders}
            onFileSelect={onFileSelect}
            onFileOpen={onFileOpen}
            onToggleFolder={onToggleFolder}
          />
          {emptyState}
        </>
      ) : undefined}
    </UIFileExplorer>

    <CreateMetadataModal
      open={createModalOpen}
      onClose={handleCloseCreateModal}
      metadataType={createModalMetadataType}
      onSubmit={handleCreateSubmit}
      isGenerating={isGeneratingTemplate}
    />
    </>
  );
}
